import { propertyFetch } from '../services/idxPropertyClient';
import { searchSnapshot, searchQuery, assertValidSearch, readSearchError } from './propertySearchFilters';
const present = (value) => value !== undefined && value !== null && value !== '';
const normalize = (value) => String(value ?? '').trim().toLowerCase();
const number = (value) => present(value) && typeof value !== 'boolean' && Number.isFinite(Number(value)) ? Number(value) : null;
const subtypeTokens=value=>String(value||'').split(',').map(v=>v.replace(/\s/g,'').toLowerCase().replace('singlefamily-detached','singlefamilyresidence'));
const values = (value) => Array.isArray(value) ? value : [value];

export function chatSearchSnapshot(params, fallbackState) {
  const snapshot = searchSnapshot(params, fallbackState);
  delete snapshot.hasMedia;
  delete snapshot.firstPhoto;
  return snapshot;
}
export const buildChatSearchParams = searchQuery;

const BOOLEAN_FIELDS = {
  newConstruction: 'new_construction_yn', hasPrivatePool: 'pool_private_yn',
  waterfront: 'waterfront_yn', hasBasement: 'basement_yn', hasAttachedGarage: 'attached_garage_yn',
  hasFireplace: 'fireplace_yn'
};
const privateBoolean = (value) => {
  if (value === true || value === 1 || value === '1') return true;
  if (value === false || value === 0 || value === '0') return false;
  return null;
};
const RANGE_FIELDS = {
  minPrice: ['current_price', 'min'], maxPrice: ['current_price', 'max'],
  minBedrooms: ['beds', 'min'], maxBedrooms: ['beds', 'max'],
  minBathrooms: ['baths', 'min'], maxBathrooms: ['baths', 'max'],
  minSqft: ['square_footage', 'min'], maxSqft: ['square_footage', 'max'],
  minLotSizeAcres: ['lot_size_acres', 'min'], maxLotSizeAcres: ['lot_size_acres', 'max'],
  minYearBuilt: ['year_built', 'min'], maxYearBuilt: ['year_built', 'max']
};

export function privateListingMatches(listing, params) {
  if (normalize(listing.status) !== 'available') return false;
  const type = normalize(listing.property_type);
  const listingType = normalize(listing.listing_type);
  const category = type === 'residential' && listingType === 'sale' ? 'Residential'
    : type === 'residential' && listingType === 'lease' ? 'ResidentialLease'
      : type === 'land' && listingType === 'sale' ? 'Land' : null;
  if (!values(params.propertyType || 'Residential').some(value => normalize(value) === normalize(category))) return false;
  const hasRadius = ['centerLat','centerLng','radiusMiles'].some(key=>present(params[key]));
  const hasBounds = ['north','south','east','west'].some(key=>present(params[key]));
  if(hasRadius||hasBounds) {
    const lat=number(listing.latitude),lng=number(listing.longitude);
    if(lat===null||lng===null) return false;
    if(hasBounds) {
      const n=number(params.north),s=number(params.south),e=number(params.east),w=number(params.west);
      if([n,s,e,w].some(v=>v===null)||lat<s||lat>n||!(w>e?(lng>=w||lng<=e):(lng>=w&&lng<=e))) return false;
    }
    if(hasRadius) {
      const n=number(params.centerLat),e=number(params.centerLng),radius=number(params.radiusMiles);
      if([n,e,radius].some(v=>v===null)||radius<0) return false;
      const rad=v=>v*Math.PI/180;
      const a=Math.sin(rad(lat-n)/2)**2+Math.cos(rad(lat))*Math.cos(rad(n))*Math.sin(rad(lng-e)/2)**2;
      if(3958.8*2*Math.asin(Math.sqrt(Math.min(1,Math.max(0,a))))>radius) return false;
    }
  }
  for (const [key, expected] of Object.entries(params)) {
    if (key === 'allPropertySubTypes') continue;
    if(['centerLat','centerLng','radiusMiles','north','south','east','west'].includes(key))continue;
    if (key === 'propertyType' || key === 'hasMedia' || key === 'firstPhoto') continue;
    if (key === 'priceSort') {
      // IDX's balanced ordering uses a database-wide average we don't receive.
      if (expected === 'balanced' || number(listing.current_price) === null) return false;
      continue;
    }
    if (key in RANGE_FIELDS) {
      const [field, direction] = RANGE_FIELDS[key];
      const actual = number(listing[field]);
      const bound = number(expected);
      if (actual === null || bound === null || (direction === 'min' ? actual < bound : actual > bound)) return false;
    } else if (key in BOOLEAN_FIELDS) {
      const actual = privateBoolean(listing[BOOLEAN_FIELDS[key]]);
      if (typeof expected !== 'boolean' || actual === null || actual !== expected) return false;
    } else if (['state', 'city', 'zip'].includes(key)) {
      const field = { state: 'state_province', city: 'city', zip: 'postal_code' }[key];
      if (!values(expected).some(value => normalize(listing[field]) === normalize(value))) return false;
    } else if (key === 'propertySubType') {
      if (!listing.property_sub_type || !values(expected).some(value => subtypeTokens(value).some(token=>subtypeTokens(listing.property_sub_type).includes(token)))) return false;
    } else {
      // Current private-listing records lack verified agent IDs, feature lists,
      // stories, and MLS listing-date/DOM fields. Never guess from remarks.
      return false;
    }
  }
  return true;
}

export function mergeChatProperties(mlsProperties, privateRows, params) {
  const privateListings = privateRows.map(listing => ({
    ...listing,
    listing_key: `PRIVATE_${listing.id}`,
    current_price: listing.listing_type === 'sale' ? listing.price_sale : listing.price_lease_monthly,
    bedrooms_total: listing.beds,
    address: `${listing.address_line_1}, ${listing.city}, ${listing.state_province}`,
    isPrivate: true
  })).filter(listing => privateListingMatches(listing, params));
  const results = [...mlsProperties, ...privateListings];
  if (params.priceSort === 'lowhigh' || params.priceSort === 'highlow') {
    const direction = params.priceSort === 'lowhigh' ? 1 : -1;
    results.sort((a, b) => {
      const left = number(a.current_price ?? a.list_price), right = number(b.current_price ?? b.list_price);
      if (left === null) return right === null ? 0 : 1;
      if (right === null) return -1;
      return direction * (left - right);
    });
  }
  return results;
}

export async function loadChatProperties({ params, siteId,  privateApiKey, fetchImpl = fetch, queryOptions = {} }) {
  assertValidSearch(params);
  const query = buildChatSearchParams(params);
  if (Number.isInteger(queryOptions.limit) && queryOptions.limit > 0) query.set("limit", String(queryOptions.limit));
  // Site presentation options stay outside the conversational filter snapshot.
  ['firstPhoto', 'hasMedia'].forEach(key => {
    if (present(queryOptions[key])) query.set(key, String(queryOptions[key]));
  });
  const privateQuery = new URLSearchParams({ site_id: siteId, status: 'available' });
  const [mlsResult, privateResult] = await Promise.allSettled([
    propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-summary?${query}`, {}, fetchImpl),
    fetchImpl(`https://tophamtechbe.com/api/treas/private/v1/listings?${privateQuery}`, { headers: { 'x-api-key': privateApiKey } })
  ]);
  if (mlsResult.status !== 'fulfilled') throw new Error('MLS search unavailable');
  if (!mlsResult.value.ok) throw new Error(await readSearchError(mlsResult.value));
  const data = await mlsResult.value.json();
  if (!Array.isArray(data?.properties)) throw new Error('Invalid MLS response');
  let privateRows = [];
  let privateUnavailable = false;
  try {
    if (privateResult.status !== 'fulfilled' || !privateResult.value.ok) throw new Error('Private search unavailable');
    const privateData = await privateResult.value.json();
    if (!Array.isArray(privateData?.listings)) throw new Error('Invalid private response');
    privateRows = privateData.listings;
  } catch {
    privateUnavailable = true;
  }
  return { properties: mergeChatProperties(data.properties, privateRows, params), privateUnavailable, searchMeta: data.search_meta };
}
